import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation} from '@github-ui/react-query'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useSuggestionBatch, useSuggestionBatchActions, useAllMarkers} from '../stores/selectors'
import {getSuggestionsFromSuggestionIds, type SuggestionChange} from '../helpers/extract-suggestions-from-comments'
import {clearActiveBatchFromStorage} from '../helpers/suggestion-batch-storage'

/**
 * Applies a batch of suggested changes by extracting suggestions from comments
 * stored in the active batch
 */
export function useApplyBatchedSuggestionsMutation(pullRequestPath: string, headOid: string) {
  const apiUrl = usePageDataUrl(PageData.submitSuggestedChanges)
  const suggestionBatch = useSuggestionBatch()
  const markers = useAllMarkers()
  const {clearSuggestionBatch} = useSuggestionBatchActions()

  return useMutation({
    mutationFn: async ({currentOid, message}: {currentOid: string; message: string}) => {
      const changes: SuggestionChange[] = getSuggestionsFromSuggestionIds(markers, suggestionBatch.activeBatch)

      if (changes.length === 0) {
        throw new Error('No valid suggestions found in batch')
      }

      const response = await reactFetchJSON(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {
          changes,
          currentOid,
          message,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      clearSuggestionBatch()
      clearActiveBatchFromStorage(pullRequestPath, headOid)

      // Reload the page to get the updated diff
      ssrSafeWindow?.location.reload()
    },
  })
}
